import React, { useState } from "react";
import { projectsData } from "../data/mock";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "./ui/carousel";
import { Star, CheckCircle, Play, Image as ImageIcon } from "lucide-react";

const MediaGallery = ({ project }) => {
  const hasGallery = project.gallery && project.gallery.length > 0;
  const hasVideo = !!project.video;

  if (!hasGallery && !hasVideo) {
    return (
      <div className="relative group overflow-hidden rounded-2xl">
        <img
          src={project.image}
          alt={project.title}
          className="w-full h-[400px] object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent" />
        <Badge className="absolute top-4 left-4 bg-teal-500/90 text-white font-nunito font-bold">
          {project.subtitle}
        </Badge>
      </div>
    );
  }

  const mediaItems = [];
  if (hasGallery) {
    project.gallery.forEach((img, i) => {
      mediaItems.push({ type: "image", src: img, key: `img-${i}` });
    });
  }
  if (hasVideo) {
    mediaItems.push({ type: "video", src: project.video, key: "video-0" });
  }

  return (
    <div className="relative">
      <Carousel opts={{ loop: true }} className="w-full">
        <CarouselContent>
          {mediaItems.map((item) => (
            <CarouselItem key={item.key}>
              <div className="relative overflow-hidden rounded-2xl">
                {item.type === "image" ? (
                  <img src={item.src} alt={project.title} className="w-full h-[400px] object-cover" />
                ) : (
                  <video
                    src={item.src}
                    controls
                    playsInline
                    preload="metadata"
                    className="w-full h-[400px] object-cover bg-black rounded-2xl"
                    poster={project.image}
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 via-transparent to-transparent pointer-events-none" />
                {item.type === "video" && (
                  <Badge className="absolute top-4 right-4 bg-slate-900/80 text-teal-400 font-nunito font-bold border border-teal-500/30 pointer-events-none">
                    <Play className="w-3 h-3 mr-1" /> Video
                  </Badge>
                )}
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className="left-3 bg-slate-900/70 border-slate-700/50 text-white hover:bg-teal-500 hover:text-white hover:border-teal-500" />
        <CarouselNext className="right-3 bg-slate-900/70 border-slate-700/50 text-white hover:bg-teal-500 hover:text-white hover:border-teal-500" />
      </Carousel>
      <Badge className="absolute top-4 left-4 bg-teal-500/90 text-white font-nunito font-bold z-10">
        {project.subtitle}
      </Badge>
      {/* Slide count indicator */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10">
        {mediaItems.map((item, i) => (
          <div key={i} className="flex items-center justify-center">
            {item.type === "image" ? (
              <ImageIcon className="w-3 h-3 text-white/60" />
            ) : (
              <Play className="w-3 h-3 text-teal-400" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const Projects = () => {
  const [hoveredProject, setHoveredProject] = useState(null);

  return (
    <section id="projects" className="relative py-24 md:py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-teal-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 backdrop-blur-md rounded-2xl py-10">
        {/* Section header */}
        <div className="flex items-center gap-3 mb-6">
          <Star className="w-5 h-5 text-teal-400" />
          <span className="font-nunito text-teal-400 text-sm tracking-[0.25em] uppercase font-semibold">
            Proyectos Destacados
          </span>
          <div className="flex-1 h-px bg-teal-500/20" />
        </div>

        <h2 className="font-bebas text-5xl md:text-6xl tracking-wide mb-16">
          <span className="text-black lg:text-teal-400">Creando</span> <span className="text-teal-400">experiencias</span>{" "}
          <span className="text-black lg:text-teal-400">únicas</span>
        </h2>

        {/* Desktop: Tabs */}
        <div className="hidden lg:block">
          <Tabs defaultValue="0" className="w-full">
            <TabsList className="backdrop-blur-md border border-slate-300/50 p-1 rounded-xl mb-10 w-full max-w-2xl">
              {projectsData.map((project, idx) => (
                <TabsTrigger
                  key={idx}
                  value={String(idx)}
                  className="flex-1 font-nunito font-semibold text-sm data-[state=active]:bg-teal-700 data-[state=active]:text-white rounded-lg py-2.5 transition-all duration-300"
                >
                  {project.title}
                </TabsTrigger>
              ))}
            </TabsList>

            {projectsData.map((project, idx) => (
              <TabsContent key={idx} value={String(idx)}>
                <div className="grid lg:grid-cols-2 gap-10 items-center">
                  {/* Media gallery or single image */}
                  <MediaGallery project={project} />

                  {/* Content */}
                  <div className="space-y-6 rounded-2xl p-6">
                    <h3 className="font-bebas text-4xl text-black tracking-wide">{project.title}</h3>
                    <p className="font-nunito text-lg text-slate-200 leading-relaxed">{project.description}</p>
                    <ul className="space-y-3">
                      {project.details.map((detail, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-teal-400 mt-0.5 shrink-0" />
                          <span className="font-nunito text-slate-200">{detail}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="font-nunito text-teal-400/80 italic text-sm border-l-2 border-teal-500/30 pl-4">
                      {project.tagline}
                    </p>
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>

        {/* Mobile cards */}
        <div className="lg:hidden space-y-8">
          {projectsData.map((project, idx) => (
            <Card
              key={idx}
              className="backdrop-blur-md border-slate-300/50 overflow-hidden hover:border-teal-500/30 transition-all duration-500"
              onMouseEnter={() => setHoveredProject(idx)}
              onMouseLeave={() => setHoveredProject(null)}
            >
              <div className="relative overflow-hidden">
                {project.gallery ? (
                  <MediaGallery project={project} />
                ) : (
                  <>
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-56 object-cover transition-transform duration-700"
                      style={{
                        transform: hoveredProject === idx ? "scale(1.05)" : "scale(1)",
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent" />
                    <Badge className="absolute top-4 left-4 bg-teal-500/90 text-white font-nunito font-bold">
                      {project.subtitle}
                    </Badge>
                  </>
                )}
              </div>
              <CardContent className="p-6">
                <h3 className="font-bebas text-3xl text-white tracking-wide mb-3">{project.title}</h3>
                <p className="font-nunito text-sm text-slate-200 leading-relaxed mb-4">{project.description}</p>
                <ul className="space-y-2 mb-4">
                  {project.details.map((detail, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-teal-400 mt-0.5 shrink-0" />
                      <span className="font-nunito text-sm text-slate-200">{detail}</span>
                    </li>
                  ))}
                </ul>
                <p className="font-nunito text-teal-400/80 italic text-xs">{project.tagline}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
